import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, Phone, Mail, ChevronDown } from "lucide-react";
import { LOGO, CONTACT } from "../../data/content";
import { SearchModal } from "../SearchModal";
import { cartCount, CART_EVENT } from "../../lib/cart";
import { themeFor } from "../../lib/theme";

const CartBadge = () => {
  const [n, setN] = useState(cartCount());
  useEffect(() => {
    const h = () => setN(cartCount());
    window.addEventListener(CART_EVENT, h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener(CART_EVENT, h);
      window.removeEventListener("storage", h);
    };
  }, []);
  return (
    <Link to="/cart" className="relative p-2 text-slate-200 hover:text-nx-goldlight transition-colors" data-testid="nav-cart" aria-label="Shopping cart">
      <ShoppingCart size={19} />
      {n > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-0.5 rounded-full bg-nx-goldlight text-nx-navy text-[10px] font-bold flex items-center justify-center" data-testid="nav-cart-count">
          {n}
        </span>
      )}
    </Link>
  );
};

const SERVICE_MENU = [
  ["Software Services", "/services/software-services"],
  ["CCTV Solutions", "/services/cctv-solutions"],
  ["Access Control Systems", "/services/access-control-systems"],
  ["Biometric Attendance Systems", "/services/biometric-attendance"],
  ["Video Door Phones", "/services/video-door-phones"],
  ["Networking Solutions", "/services/networking-solutions"],
  ["Structured Cabling", "/services/structured-cabling"],
  ["Fire Alarm Systems", "/services/fire-alarm-systems"],
  ["EPABX & Intercom", "/services/epabx-intercom"],
  ["UPS & Power Solutions", "/services/ups-power-solutions"],
  ["Annual Maintenance (AMC)", "/services/amc"],
];

const ABOUT_MENU = [
  ["About Us", "/about"],
  ["Brands We Deal With", "/brands-we-deal-with"],
];

const NAV_MENUS = { "/services": SERVICE_MENU, "/about": ABOUT_MENU };

export const SITE_NAV = [
  { label: "Solutions", to: "/solutions" },
  { label: "Services", to: "/services" },  { label: "Industries", to: "/industries" },
  { label: "Projects", to: "/projects" },
  { label: "About", to: "/about" },
  { label: "Technology", to: "/technology" },
  { label: "Insights", to: "/insights" },
  { label: "Products", to: "/store" },
  { label: "Downloads", to: "/downloads" },
  { label: "Support", to: "/support" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
  { label: "Inquiries", to: "/inquiries" },
];

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = themeFor(pathname);
  const blue = theme === "blue" || theme === "pan";
  const logoIcon = theme === "pan" ? LOGO.iconPan : blue ? LOGO.iconBlue : LOGO.iconDark;
  const logoName = theme === "pan" ? LOGO.namePan : blue ? LOGO.nameBlue : LOGO.nameDark;

  const isActive = (to) => pathname === to || pathname.startsWith(to + "/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHome = (e) => {
    e.preventDefault();
    if (pathname === "/") {
      window.scrollTo(0, 0);
      window.dispatchEvent(new Event("resize"));
    } else {
      navigate("/");
    }
  };

  return (
    <>
      <div className="bg-[#060d1a] border-b border-white/5" data-testid="top-bar">
        <div className="nx-wrap flex items-center justify-between h-9 text-[11px] tracking-wide text-slate-400">
          <div className="flex items-center gap-4 sm:gap-6 min-w-0">
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 hover:text-nx-goldlight transition-colors" data-testid="top-bar-phone">
              <Phone size={11} className="text-nx-goldlight shrink-0" /> {CONTACT.phone}
              <span className="hidden sm:inline text-slate-600">|</span>
              <span className="hidden sm:inline">{CONTACT.phone2}</span>
            </a>
          </div>
          <span className="flex items-center gap-1.5 shrink-0" data-testid="top-bar-email">
            <Mail size={11} className="text-nx-goldlight shrink-0" /> {CONTACT.info}
          </span>
        </div>
      </div>
      <header className={`fixed inset-x-0 z-40 bg-nx-navy/90 backdrop-blur-md border-b border-white/10 transition-[top] duration-300 ${scrolled ? "top-0" : "top-9"}`} data-testid="site-header">
      <div className="nx-wrap flex items-center justify-between h-16 sm:h-20">
        <a href="/" onClick={goHome} className="flex items-center gap-3.5 cursor-pointer" data-testid="site-logo" aria-label="NexSecure Technologies — home">
          <img src={logoIcon} alt="NexSecure emblem" className="h-10 sm:h-12 w-auto" />
          <span className="flex flex-col justify-center">
            <img src={logoName} alt="NexSecure" className="h-4 sm:h-6 w-auto" />
            <span className={`text-[8px] sm:text-[9px] tracking-[0.42em] uppercase mt-0.5 ${blue ? "text-white" : "text-[#efe9dc]"}`}>Technologies</span>
          </span>
        </a>
        <div className="flex items-center gap-2">
          <CartBadge />
          <SearchModal />
          <button className="xl:hidden text-slate-200 p-2" onClick={() => setOpen(!open)} data-testid="site-mobile-menu-button" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <nav className="hidden xl:flex justify-center border-t border-white/5 overflow-x-auto" data-testid="site-nav" aria-label="Primary">
        <div className="flex items-center whitespace-nowrap">
          {SITE_NAV.map((n) => {
            const menu = NAV_MENUS[n.to];
            if (!menu) {
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`relative text-[11px] tracking-[0.18em] uppercase px-3 2xl:px-5 py-3 transition-colors after:absolute after:left-3 after:right-3 after:bottom-1.5 after:h-px after:bg-nx-goldlight after:transition-all after:duration-300 ${
                    isActive(n.to) ? "text-nx-goldlight after:scale-x-100" : "text-slate-300 hover:text-nx-goldlight after:scale-x-0 hover:after:scale-x-100"
                  }`}
                >
                  {n.label}
                </Link>
              );
            }
            const key = n.label.toLowerCase();
            return (
              <div key={n.to} className="relative group">
                <Link
                  to={n.to}
                  className={`relative inline-block text-[11px] tracking-[0.18em] uppercase px-3 2xl:px-5 py-3 transition-colors after:absolute after:left-3 after:right-3 after:bottom-1.5 after:h-px after:bg-nx-goldlight after:transition-all after:duration-300 ${
                    isActive(n.to) ? "text-nx-goldlight after:scale-x-100" : "text-slate-300 hover:text-nx-goldlight after:scale-x-0 group-hover:after:scale-x-100"
                  }`}
                >
                  {n.label}
                </Link>
                <div className={`absolute left-1/2 -translate-x-1/2 top-full hidden group-hover:block pt-2 z-50`} data-testid={`nav-${key}-menu`}>
                  <div className={`bg-[#060d1a] border border-white/10 shadow-2xl shadow-black/50 grid ${menu.length > 5 ? "grid-cols-2 gap-x-8 min-w-[32rem]" : "grid-cols-1 min-w-[16rem]"} gap-y-3.5 p-7`}>
                    {menu.map(([label, to]) => (
                      <Link key={to} to={to} className="text-[11px] tracking-[0.14em] uppercase text-slate-300 hover:text-nx-goldlight transition-colors" data-testid={`nav-${key}-${to.split("/").pop()}`}>
                        {label}
                      </Link>
                    ))}
                    {n.to === "/services" && (
                      <Link to="/services" className="col-span-2 mt-1 text-[11px] tracking-[0.14em] uppercase text-nx-goldlight hover:text-white transition-colors" data-testid="nav-service-all">
                        All services →
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </nav>
      {open && (
        <nav className="xl:hidden border-t border-white/10 bg-nx-navy/95 backdrop-blur-md px-6 py-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto" data-testid="site-mobile-nav">
          {SITE_NAV.map((n) => {
            const menu = NAV_MENUS[n.to];
            const key = n.label.toLowerCase();
            return (
              <div key={n.to} className="flex flex-col gap-4">
                {menu ? (
                  <>
                    <button
                      onClick={() => setOpenMenu(openMenu === n.to ? null : n.to)}
                      className={`flex items-center justify-between text-left text-xs tracking-[0.22em] uppercase ${isActive(n.to) ? "text-nx-goldlight" : "text-slate-300"}`}
                      data-testid={`mobile-${key}-toggle`}
                      aria-expanded={openMenu === n.to}
                    >
                      {n.label}
                      <ChevronDown size={14} className={`transition-transform duration-300 ${openMenu === n.to ? "rotate-180" : ""}`} />
                    </button>
                    {openMenu === n.to && (
                      <div className="flex flex-col gap-3 pl-4 border-l border-white/10" data-testid={`nav-${key}-menu-mobile`}>
                        {menu.map(([label, to]) => (
                          <button
                            key={to}
                            onClick={() => { setOpen(false); setOpenMenu(null); navigate(to); }}
                            className={`text-left text-[11px] tracking-[0.14em] uppercase ${isActive(to) ? "text-nx-goldlight" : "text-slate-400"}`}
                            data-testid={`nav-${key}-mobile-${to.split("/").pop()}`}
                          >
                            {label}
                          </button>
                        ))}
                        {n.to === "/services" && (
                          <button
                            onClick={() => { setOpen(false); setOpenMenu(null); navigate("/services"); }}
                            className="text-left text-[11px] tracking-[0.14em] uppercase text-nx-goldlight"
                            data-testid="nav-service-mobile-all"
                          >
                            All services →
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => { setOpen(false); navigate(n.to); }}
                    className={`text-left text-xs tracking-[0.22em] uppercase ${isActive(n.to) ? "text-nx-goldlight" : "text-slate-300"}`}
                  >
                    {n.label}
                  </button>
                )}
              </div>
            );
          })}
        </nav>
      )}
    </header>
    </>
  );
};
